"use client";
import "@vidstack/react/player/styles/base.css";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/audio.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import { MediaPlayer, MediaProvider, Poster, Track } from "@vidstack/react";
import { DefaultVideoLayout, defaultLayoutIcons } from "@vidstack/react/player/layouts/default";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { ExtractBySectionType, SectionType } from "types";
import { cfUrlFor } from "@/sanity/lib/utils";
import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type VideoTestimonialsType = ExtractBySectionType<SectionType, "videoTestimonials", "_type">;

export function VideoTestimonials({ data }: { data: VideoTestimonialsType }) {
  const { heading, videos } = data;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const scrollToVideo = (index: number) => {
    if (scrollContainerRef.current && videos) {
      const container = scrollContainerRef.current;
      const videoElements = container.querySelectorAll("[data-video-item]");
      console.log(videoElements);
      const targetElement = videoElements[index] as HTMLElement;

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
        setCurrentIndex(index);
      }
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      scrollToVideo(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (videos && currentIndex < videos.length - 1) {
      scrollToVideo(currentIndex + 1);
    }
  };

  return (
    <section className="w-full max-w-screen-2xl mx-auto font-outfit px-4 sm:px-8 py-12 sm:py-20 sm:pb-32 flex flex-col items-center justify-center relative">
      <h3 className="font-medium max-w-[500px] text-xl lg:text-3xl text-center mx-auto mb-8 sm:mb-12 px-4">
        {heading}
      </h3>

      {/* Container with gradient fade indicators */}
      <div className="relative w-full max-w-full">
        {/* Left fade gradient - hidden on single video view */}
        {videos && videos.length > 1 && (
          <div className="hidden sm:block absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        )}

        {/* Right fade gradient - visible to indicate scrollability */}
        {videos && videos.length > 1 && (
          <div className="absolute right-0 top-0 bottom-0 w-4 sm:w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
        )}

        <ScrollArea
          style={{
            width: "100%",
            margin: "0 auto",
          }}
          className="lg:max-w-screen-xl"
          ref={scrollContainerRef}
        >
          <div
            className="flex flex-row gap-4 sm:gap-6 lg:gap-8 pb-4 px-4 sm:px-8"
            style={{
              scrollSnapType: "x mandatory",
            }}
          >
            {videos &&
              videos.map((video, index) => (
                <div
                  data-video-item
                  className="flex-shrink-0 w-[min(320px,85vw)] sm:w-[340px] lg:w-[400px]"
                  key={index}
                  style={{ scrollSnapAlign: "center" }}
                >
                  <MediaPlayer
                    aspectRatio="9 / 16"
                    src={video.url}
                    viewType="video"
                    streamType="on-demand"
                    logLevel="warn"
                    crossOrigin
                    playsInline
                    title={video.title}
                    poster={cfUrlFor(video.thumbnail)}
                    className="rounded-lg overflow-hidden shadow-lg"
                  >
                    <MediaProvider>
                      <Poster className="vds-poster" />
                      {video.tracks &&
                        video.tracks.map((track) => (
                          <Track
                            label={track.label}
                            src={""}
                            language={track.language.code}
                            key={track.label}
                            kind={track.kind}
                          />
                        ))}
                    </MediaProvider>
                    <DefaultVideoLayout icons={defaultLayoutIcons} className="vds-layout" />
                  </MediaPlayer>
                </div>
              ))}
          </div>
          <ScrollBar
            orientation="horizontal"
            className="h-2 sm:h-3 bg-gray-200 rounded-full hidden"
          />
        </ScrollArea>
      </div>

      {/* Navigation Buttons - Bottom Right */}

      {videos && videos.length > 1 && (
        <div className="absolute flex gap-3 mt-6 justify-end max-w-full px-4 sm:px-8 bottom-10 z-20 right-2">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="bg-white rounded-full p-3 sm:p-4 shadow-lg border border-gray-200
                       transition-all duration-200
                       hover:bg-gray-50
                       disabled:opacity-50 disabled:cursor-not-allowed
                       disabled:hover:bg-white"
            aria-label="Previous video"
          >
            <ChevronLeft
              className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors ${
                currentIndex === 0 ? "text-gray-400" : "text-gray-800"
              }`}
            />
          </button>

          <button
            onClick={handleNext}
            disabled={currentIndex === videos.length - 1}
            className="bg-white rounded-full p-3 sm:p-4 shadow-lg border border-gray-200
                       transition-all duration-200
                       hover:bg-gray-50
                       disabled:opacity-50 disabled:cursor-not-allowed
                       disabled:hover:bg-white"
            aria-label="Next video"
          >
            <ChevronRight
              className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors ${
                currentIndex === videos.length - 1 ? "text-gray-400" : "text-gray-800"
              }`}
            />
          </button>
        </div>
      )}

      {/* Optional: Indicator dots for mobile */}
      {videos && videos.length > 1 && videos.length <= 5 && (
        <div className="flex gap-2 mt-4 sm:hidden">
          {videos.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? "bg-gray-800" : "bg-gray-300"
              }`}
              aria-label={`Video ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
